'use client';

import { useId, useState } from 'react';
import { cn } from '@/lib/tailwind';
import { CONSENT_BANNER_COPY, CONSENT_CATEGORIES } from './consent.constants';
import type { ServiceConsentState } from './consent.types';
import {
    isCategorySelected,
    isServiceSelected,
    selectCategory,
    selectService,
    servicesInCategory,
} from './consent-services';
import { useConsent } from './ConsentProvider';
import { ConsentCategoryItem } from './ConsentCategoryItem';

// Filled primary button (the site `.btn` is a white outline; invert it for emphasis).
const PRIMARY_BTN = 'btn bg-white [--btn-color:#171717] [--btn-color-hover:#171717] hover:bg-white/90';
const OUTLINE_BTN = 'btn';
const TEXT_BTN = 'cursor-pointer text-sm text-white/70 underline underline-offset-4 transition-colors hover:text-white';

function CloseIcon() {
    return (
        <svg viewBox="0 0 20 20" fill="none" className="size-4" aria-hidden="true">
            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}

export function CookieBanner() {
    const { isBannerVisible, view, setView, hasDecision, undecidedServices, closeBanner, acceptAll, rejectAll } =
        useConsent();
    const headingId = useId();

    if (!isBannerVisible) return null;

    // Services added after an earlier decision: say so instead of repeating the
    // first-visit copy, and keep the existing selection pre-filled underneath.
    const isUpdate = hasDecision && undecidedServices.length > 0;
    const compactTitle = isUpdate ? CONSENT_BANNER_COPY.updateTitle : CONSENT_BANNER_COPY.title;
    const compactDescription = isUpdate ? CONSENT_BANNER_COPY.updateDescription : CONSENT_BANNER_COPY.description;

    return (
        <section
            role="dialog"
            aria-modal="false"
            aria-labelledby={headingId}
            aria-label="Datenschutz-Hinweis"
            className="fixed inset-x-0 bottom-0 z-40"
        >
            <div className="pointer-events-none p-3 sm:p-4">
                <div
                    className={cn(
                        'pointer-events-auto relative mx-auto flex max-w-page-sm flex-col overflow-y-auto',
                        'max-h-[calc(100dvh-5rem)] bg-body-secondary text-white/85',
                        'p-5 sm:p-6',
                    )}
                >
                    {hasDecision && (
                        <button
                            type="button"
                            onClick={closeBanner}
                            aria-label={CONSENT_BANNER_COPY.close}
                            className="absolute end-3 top-3 inline-flex size-8 cursor-pointer items-center justify-center text-white/60 transition-colors hover:text-white"
                        >
                            <CloseIcon />
                        </button>
                    )}

                    {view === 'compact' ? (
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
                            <div className="space-y-1.5">
                                <h2 id={headingId} className="text-base font-medium text-white">
                                    {compactTitle}
                                </h2>
                                <p className="text-sm text-white/70">{compactDescription}</p>
                            </div>

                            <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center lg:flex-nowrap">
                                <button
                                    type="button"
                                    onClick={() => setView('settings')}
                                    className={cn(TEXT_BTN, 'sm:me-2 my-3 sm:my-0')}
                                >
                                    {CONSENT_BANNER_COPY.configure}
                                </button>
                                <button type="button" onClick={rejectAll} className={OUTLINE_BTN}>
                                    {CONSENT_BANNER_COPY.rejectAll}
                                </button>
                                <button type="button" onClick={acceptAll} className={PRIMARY_BTN}>
                                    {CONSENT_BANNER_COPY.acceptAll}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <ConsentSettingsPanel headingId={headingId} />
                    )}
                </div>
            </div>
        </section>
    );
}

function ConsentSettingsPanel({ headingId }: { headingId: string }) {
    const { serviceConsent, services, save, acceptAll, rejectAll, setView } = useConsent();
    // Initialized once on mount; the panel remounts when the view toggles, so it
    // always picks up the current consent without a synchronizing effect. New
    // services simply have no entry yet and therefore start switched off.
    const [selection, setSelection] = useState<ServiceConsentState>(() => serviceConsent);

    // Only list categories the catalog actually covers. Required categories
    // (necessary cookies) always stay visible as a disclosure.
    const visibleCategories = CONSENT_CATEGORIES.filter(
        (category) => category.required || services.some((service) => service.category === category.key),
    );

    return (
        <div className="flex flex-col gap-4">
            {/* Back to the initial compact banner with the three buttons. */}
            <button
                type="button"
                onClick={() => setView('compact')}
                className="-ml-0.5 inline-flex shrink-0 cursor-pointer items-center gap-1.5 self-start text-sm text-white/70 transition-colors hover:text-white"
            >
                <svg viewBox="0 0 20 20" fill="none" className="size-4" aria-hidden="true">
                    <path
                        d="M12.5 5l-5 5 5 5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                {CONSENT_BANNER_COPY.back}
            </button>

            <div className="space-y-4">
                <div className="space-y-1.5 pe-8">
                    <h2 id={headingId} className="text-base font-medium text-white">
                        {CONSENT_BANNER_COPY.settingsTitle}
                    </h2>
                    <p className="text-sm text-white/70">{CONSENT_BANNER_COPY.settingsDescription}</p>
                </div>

                <ul className="-my-1 divide-y divide-white/10">
                    {visibleCategories.map((category) => {
                        const categoryServices = servicesInCategory(services, category.key);

                        return (
                            <ConsentCategoryItem
                                key={category.key}
                                category={category}
                                services={categoryServices}
                                checked={isCategorySelected(selection, category.key, categoryServices)}
                                isServiceChecked={(service) => isServiceSelected(selection, service)}
                                onCategoryChange={(checked) =>
                                    setSelection((prev) => selectCategory(prev, categoryServices, checked))
                                }
                                onServiceChange={(service, checked) =>
                                    setSelection((prev) => selectService(prev, service, checked))
                                }
                            />
                        );
                    })}
                </ul>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button type="button" onClick={rejectAll} className={OUTLINE_BTN}>
                    {CONSENT_BANNER_COPY.rejectAll}
                </button>
                <button type="button" onClick={acceptAll} className={OUTLINE_BTN}>
                    {CONSENT_BANNER_COPY.acceptAll}
                </button>
                <button type="button" onClick={() => save(selection)} className={PRIMARY_BTN}>
                    {CONSENT_BANNER_COPY.save}
                </button>
            </div>
        </div>
    );
}
